import React, { createContext, useContext, useState, useEffect } from 'react';
import { useRouter } from "next/router";
import BusinessContext from "./business-context";
import ContactContext from './contact-context';
import DomainContext from './domain-context';
import { renderTables, renderRows, onCreateRow, OnCreateTable, renderPegs } from '../lib/table';
import { extractEmailAddress, harmonizeEmail } from '../lib/mail-utils';
import { add } from 'date-fns';

// Create the context
const TableMailContext = createContext({
    inbox: null,
    setInbox: () => { },
    mails: null,
    setMails: () => { },
    mailRows: {},
    setMailRows: () => { },
    threads: {},
    setThreads: () => { },
    fetchMails: () => { },
    fetchNext50Tables: () => { },
    fetchPrevious50Tables: () => { },
    replyToMail: () => { },
    composeMail: () => { },
    isMailFetching: false,
    setIsMailFetching: () => { },
    setLoadingAllMails: () => { },
    loadingAllMails: false,
    currentTablePage: 0,
    setCurrentTablePage: () => { },
    totalTables: [],
    setTotalTables: () => { },
    hasNextPage: false,
    hasPreviousPage: false,
    namespace: null,
});

// Provider component
export function TableMailProvider({ children }) {
    const { activeBusiness, setNotificationContent } = useContext(BusinessContext);
    const router = useRouter();

    const [inbox, setInbox] = useState(null);
    const [mails, setMails] = useState(null);
    const [mailRows, setMailRows] = useState({});
    const [threads, setThreads] = useState({});
    const [isMailFetching, setIsMailFetching] = useState(false);
    const [loadingAllMails, setLoadingAllMails] = useState(false);
    const [currentTablePage, setCurrentTablePage] = useState(0);
    const [totalTables, setTotalTables] = useState([]);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPreviousPage, setHasPreviousPage] = useState(false);
    const { userEmail } = useContext(DomainContext);
    const { updateContactList } = useContext(ContactContext);

    let namespace

    if (router.pathname.includes('inbox')) {
        namespace = 'inbox';
    }
    else if (router.pathname.includes('sent')) {
        namespace = 'sent';
    }

    const TABLES_PER_PAGE = 50;

    const isNamespaceValid = (ns) => {
        const validNs = ["inbox", "sent"]
        if (!validNs.includes(ns)) {
            return false;
        }
        return true;
    }
    useEffect(() => {
        if (activeBusiness && userEmail) {
            // Clear existing data immediately when route changes to prevent brief content flash
            setInbox(null);
            setMails([]);
            setMailRows({});
            setThreads({});
            setLoadingAllMails(true);
            fetchMails(true);
        }
    }, [activeBusiness, router, userEmail]);

    useEffect(() => {
        if (inbox?.length === 0 || inbox === null) {
            setMails([]);
        }
    }, [inbox]);



    const fetchMails = (firstRender = false, pageNumber = null, isRefresh = false) => {
        setIsMailFetching(true);
        if (firstRender && !isRefresh) {
            setLoadingAllMails(true);
        }

        const onError = (error) => {
            console.error("Error fetching mails:", error);
            setIsMailFetching(false);
            setLoadingAllMails(false);
        };

        const callback = (data) => {
            // Store all tables for pagination
            const allTables = Array.isArray(data) ? data : [data].filter(Boolean);

            // Sort tables by creation date (latest first)
            const sortedTables = [...allTables].sort((a, b) => {
                const dateA = new Date(a.created_at || 0);
                const dateB = new Date(b.created_at || 0);
                return dateB - dateA;
            });

            // Always update totalTables when fetching from server
            if (firstRender || isRefresh || totalTables.length === 0) {
                setTotalTables(sortedTables);
                // Reset to first page only on initial load, not on refresh
                if (totalTables.length === 0) {
                    setCurrentTablePage(0);
                }
            }

            // Use the provided page number, current page, or 0 for initial load
            const pageToFetch = pageNumber !== null ? pageNumber :
                (totalTables.length === 0 ? 0 : currentTablePage);

            // Calculate pagination using sorted data
            const startIndex = pageToFetch * TABLES_PER_PAGE;
            const endIndex = startIndex + TABLES_PER_PAGE;
            const tablesToProcess = sortedTables.slice(startIndex, endIndex);

            // Update pagination state
            setHasNextPage(endIndex < sortedTables.length);
            setHasPreviousPage(pageToFetch > 0);

            if (pageNumber !== null) {
                setCurrentTablePage(pageNumber);
            }

            if (!tablesToProcess || tablesToProcess.length === 0) {
                setInbox([]);
                setLoadingAllMails(false);
                setIsMailFetching(false);
                return;
            }

            let tablesProcessed = 0;
            const checkAllDone = () => {
                if (tablesProcessed === tablesToProcess.length) {
                    setInbox(tablesToProcess);
                    setLoadingAllMails(false);
                    setIsMailFetching(false);
                }
            };

            const rowsCallback = (rowData, tableId) => {
                setMailRows(prev => ({
                    ...prev,
                    [tableId]: rowData
                }));
                tablesProcessed++;
                checkAllDone();
            };

            tablesToProcess.forEach(table => {
                if (table && table.uuid) {
                    renderRows(
                        table.uuid,
                        (rowData) => rowsCallback(rowData, table.uuid),
                        activeBusiness,
                        onError,
                        router
                    );
                } else {
                    tablesProcessed++;
                    checkAllDone();
                }
            });
        };

        // Fetch tables with namespace directly
        if (isNamespaceValid(namespace)) {
            renderTables(callback, activeBusiness, onError, router, 'emails', namespace);
        }
    };

    // Standalone harmonize function for /harmonize page
    const harmonizeEmails = (onProgress = null, onComplete = null, onError = null) => {
        if (!userEmail?.address) {
            console.warn("User email not available, cannot harmonize");
            onError?.("User email not available");
            return;
        }

        const harmonizeCallback = (data) => {
            const allTables = Array.isArray(data) ? data : [data].filter(Boolean);

            if (!allTables || allTables.length === 0) {
                onComplete?.("No tables without namespace found");
                return;
            }

            let harmonizeTablesProcessed = 0;
            const totalTables = allTables.length;

            const checkHarmonizeDone = () => {
                harmonizeTablesProcessed++;
                onProgress?.(harmonizeTablesProcessed, totalTables);

                if (harmonizeTablesProcessed === totalTables) {
                    onComplete?.("Harmonization complete");
                }
            };

            const harmonizeRowsCallback = (rowData, tableId) => {
                // Harmonize each row to assign proper namespace
                if (userEmail?.address) {
                    rowData.forEach(row => {
                        harmonizeEmail(tableId, row, userEmail.address, activeBusiness, router);
                    });
                }
                checkHarmonizeDone();
            };

            const harmonizeOnError = (error) => {
                console.error("Error harmonizing emails:", error);
                checkHarmonizeDone();
            };

            allTables.forEach(table => {
                if (table && table.uuid) {
                    renderRows(
                        table.uuid,
                        (rowData) => harmonizeRowsCallback(rowData, table.uuid),
                        activeBusiness,
                        harmonizeOnError,
                        router
                    );
                } else {
                    checkHarmonizeDone();
                }
            });
        };

        const harmonizeError = (error) => {
            console.error("Error fetching tables for harmonization:", error);
            onError?.(error);
        };

        // Fetch tables without namespace for harmonization
        renderTables(harmonizeCallback, activeBusiness, harmonizeError, router, 'emails', null);
    };

    useEffect(() => {
        if (activeBusiness && loadingAllMails === false && isNamespaceValid(namespace)) {
            const intervalId = setInterval(() => {
                fetchMails(true, null, true);
            }, 60000);

            return () => clearInterval(intervalId);
        }
    }, [activeBusiness, router]);

    const fetchNext50Tables = () => {
        if (hasNextPage) {
            const nextPage = currentTablePage + 1;
            const startIndex = nextPage * TABLES_PER_PAGE;
            const endIndex = startIndex + TABLES_PER_PAGE;

            // Sort cached tables by creation date (latest first)
            const sortedTotalTables = [...totalTables].sort((a, b) => {
                const dateA = new Date(a.created_at || 0);
                const dateB = new Date(b.created_at || 0);
                return dateB - dateA;
            });

            const tablesToProcess = sortedTotalTables.slice(startIndex, endIndex);

            setIsMailFetching(true);
            setCurrentTablePage(nextPage);
            setHasNextPage(endIndex < sortedTotalTables.length);
            setHasPreviousPage(true);
            setInbox(tablesToProcess);

            if (!tablesToProcess || tablesToProcess.length === 0) {
                setIsMailFetching(false);
                return;
            }

            let tablesProcessed = 0;
            const checkAllDone = () => {
                if (tablesProcessed === tablesToProcess.length) {
                    setIsMailFetching(false);
                }
            };

            const rowsCallback = (rowData, tableId) => {
                setMailRows(prev => ({
                    ...prev,
                    [tableId]: rowData
                }));
                tablesProcessed++;
                checkAllDone();
            };

            const onError = (error) => {
                console.error("Error fetching next tables:", error);
                setIsMailFetching(false);
            };

            tablesToProcess.forEach(table => {
                if (table && table.uuid) {
                    renderRows(
                        table.uuid,
                        (rowData) => rowsCallback(rowData, table.uuid),
                        activeBusiness,
                        onError,
                        router
                    );
                } else {
                    tablesProcessed++;
                    checkAllDone();
                }
            });
        }
    };

    const fetchPrevious50Tables = () => {
        if (hasPreviousPage) {
            const prevPage = currentTablePage - 1;
            const startIndex = prevPage * TABLES_PER_PAGE;
            const endIndex = startIndex + TABLES_PER_PAGE;

            // Sort cached tables by creation date (latest first)
            const sortedTotalTables = [...totalTables].sort((a, b) => {
                const dateA = new Date(a.created_at || 0);
                const dateB = new Date(b.created_at || 0);
                return dateB - dateA;
            });

            const tablesToProcess = sortedTotalTables.slice(startIndex, endIndex);

            setIsMailFetching(true);
            setCurrentTablePage(prevPage);
            setHasNextPage(true);
            setHasPreviousPage(prevPage > 0);
            setInbox(tablesToProcess);

            if (!tablesToProcess || tablesToProcess.length === 0) {
                setIsMailFetching(false);
                return;
            }

            let tablesProcessed = 0;
            const checkAllDone = () => {
                if (tablesProcessed === tablesToProcess.length) {
                    setIsMailFetching(false);
                }
            };

            const rowsCallback = (rowData, tableId) => {
                setMailRows(prev => ({
                    ...prev,
                    [tableId]: rowData
                }));
                tablesProcessed++;
                checkAllDone();
            };

            const onError = (error) => {
                console.error("Error fetching previous tables:", error);
                setIsMailFetching(false);
            };

            tablesToProcess.forEach(table => {
                if (table && table.uuid) {
                    renderRows(
                        table.uuid,
                        (rowData) => rowsCallback(rowData, table.uuid),
                        activeBusiness,
                        onError,
                        router
                    );
                } else {
                    tablesProcessed++;
                    checkAllDone();
                }
            });
        }
    };

    const replyToMail = (thread, row, additionalCallback) => {
        setNotificationContent({
            showNotification: true,
            header: 'Sending Reply',
            message: <div>Sending your reply...</div>,
            icon: 'mail-check'
        });

        // Calculate the next position for the reply
        const tableId = thread.threadId || thread.tableId || thread.id;

        const payload = {
            ...row,
            position: Date.now() / 1000
        };
        const callback = (json) => {
            setNotificationContent({
                showNotification: true,
                header: 'Reply Sent',
                message: <div>Your Message has been sent</div>,
                icon: 'mail-check'
            });
            setTimeout(() => {
                setNotificationContent(prev => ({ ...prev, showNotification: false }));
            }, 8000);
            if (json && json.id) {
                // If the row was created successfully, update the mailRows state
                setMailRows(prev => ({
                    ...prev,
                    [tableId]: [...(prev[tableId] || []), json]
                }));

                // Pass the new reply to the additional callback
                additionalCallback?.(json);
            }
        }
        const onError = (error) => {
            setNotificationContent({
                showNotification: true,
                header: 'Error Sending Reply',
                message: <div>There was an error sending reply <strong onClick={() => { }} className='underline cursor-pointer'>Retry</strong> or <strong onClick={() => { }} className='underline cursor-pointer'>save to draft</strong></div>,
                icon: 'mail-x'
            });
            setTimeout(() => {
                setNotificationContent(prev => ({ ...prev, showNotification: false }));
            }, 8000);
            console.error("Error creating reply mail:", error);
        }
        onCreateRow(tableId, callback, payload, activeBusiness, onError, router);
    }

    const viewMessage = () => {
        router.push(`/${activeBusiness.uuid}/sent`);
    }

    const composeMail = (row, additionalCallback, additionalError) => {
        setNotificationContent({
            showNotification: true,
            header: 'Sending Mail',
            message: <div>Sending mail... <strong onClick={() => { }} className='underline cursor-pointer'>Undo</strong></div>,
            icon: 'mail-check'
        });
        const pegsCallback = (json) => {
            const tablePayload = {
                "columns": null,
                "abstract_from": "_email",
                "peg": json[0].id,
            }

            OnCreateTable(tablePayload, callback, activeBusiness, onError, router);
        }

        const onError = (error) => {
            additionalError?.(error);
            console.error("Error creating mail:", error);
            setNotificationContent({
                showNotification: true,
                header: 'Error Sending Mail',
                message: <div>There was an error sending mail <strong onClick={() => { }} className='underline cursor-pointer'>Retry</strong> or <strong onClick={() => { }} className='underline cursor-pointer'>save to draft</strong></div>,
                icon: 'mail-x'
            });
            setTimeout(() => {
                setNotificationContent(prev => ({ ...prev, showNotification: false }));
            }, 8000);
        }
        const rowCallBack = (json) => {
            fetchMails(false, null, true);
            additionalCallback?.(json);
            setNotificationContent({
                showNotification: true,
                header: 'Mail Sent',
                message: <div>Your Mail has been sent <strong onClick={() => { viewMessage() }} className='underline cursor-pointer'>View message</strong></div>,
                icon: 'mail-check'
            });
            setTimeout(() => {
                setNotificationContent(prev => ({ ...prev, showNotification: false }));
            }, 8000);
            // updateC
        }
        const callback = (json) => {
            onCreateRow(json.uuid, rowCallBack, row, activeBusiness, onError, router);
        }
        renderPegs(pegsCallback, "emails", onError, router);
    }

    useEffect(() => {
        if (inbox && Object.keys(mailRows).length > 0) {
            let formattedMails = [];
            let threadsMap = {};

            // Process inbox based on whether it's an array or single object
            const inboxArray = Array.isArray(inbox) ? inbox : [inbox];

            const sortedInboxArray = [...inboxArray].sort((a, b) => {
                const dateA = new Date(a.created_at || 0);
                const dateB = new Date(b.created_at || 0);
                return dateB - dateA;
            });

            // Collect all emails from all table rows first
            const aggregatedEmails = {
                to: [],
                cc: [],
                name: '',
                phone_no: null
            };

            sortedInboxArray.forEach(table => {
                const tableRows = mailRows[table.uuid] || [];
                
                // Collect emails from each row
                tableRows.forEach(row => {
                    if (row.data?.to && Array.isArray(row.data.to)) {
                        aggregatedEmails.to.push(...row.data.to);
                    }
                    if (row.data?.cc && Array.isArray(row.data.cc)) {
                        aggregatedEmails.cc.push(...row.data.cc);
                    }
                });

                if (Array.isArray(tableRows)) {
                    // Filter out emails where 'from' is an empty string
                    const filteredRows = tableRows.filter(row => row.data?.from && row.data.from.trim() !== '');
                    const tableEmails = filteredRows.map(row => ({
                        id: row.id,
                        tableId: table.uuid,
                        threadId: table.uuid,
                        name: row.data?.from || 'Unknown Sender',
                        email: row.data?.from || 'unknown@email.com',
                        subject: table.name === '' ? row.data.subject : table.name || '',
                        body: (!row.data?.html || row.data.html === "(No HTML content)" || row.data.html === "") ?
                            `<pre style="font-family: 'Courier New', Consolas, monospace; white-space: pre-wrap; margin: 0; font-size: 14px;">${(row.data?.plain_text || '').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>` :
                            row.data?.html,
                        plainText: (!row.data?.plain_text && (row.data?.attachments || []).length > 0) ?
                            `📎 ${(row.data?.attachments || []).length} ${(row.data?.attachments || []).length === 1 ? 'attachment' : 'attachments'}` :
                            (row.data?.plain_text || '').replace(/\n/g, ' '),
                        date: row.created_at || '',
                        cc: row.data?.cc || [],
                        to: row.data?.to || [],
                        attachments: row.data?.attachments || [],
                        isFavourite: false,
                        isImportant: false,
                        position: row.position,
                        namespace: row.namespace,
                        // Thread-specific references
                        messageId: row.data?.message_id || '',
                        inReplyTo: row.data?.in_reply_to || '',
                        references: row.data?.references || []
                    }));

                    // Add thread information
                    threadsMap[table.uuid] = {
                        threadId: table.uuid,
                        subject: table.name || '',
                        emails: tableEmails,
                        latestDate: tableEmails.reduce((latest, email) => {
                            const emailDate = new Date(email.date);
                            return emailDate > latest ? emailDate : latest;
                        }, new Date(0))
                    };

                    // Still add individual emails to formattedMails for backward compatibility
                    formattedMails = [...formattedMails, ...tableEmails];
                }
            });

            // Update contact list once with all aggregated emails
            if (aggregatedEmails.to.length > 0 || aggregatedEmails.cc.length > 0) {
                updateContactList(aggregatedEmails);
            }

            setMails(formattedMails);
            setThreads(threadsMap);
        }
    }, [inbox, mailRows]);

    const value = {
        inbox,
        setInbox,
        mails,
        setMails,
        mailRows,
        setMailRows,
        threads,
        setThreads,
        fetchMails,
        fetchNext50Tables,
        fetchPrevious50Tables,
        replyToMail,
        composeMail,
        harmonizeEmails,
        isMailFetching,
        setIsMailFetching,
        setLoadingAllMails,
        loadingAllMails,
        currentTablePage,
        setCurrentTablePage,
        totalTables,
        setTotalTables,
        hasNextPage,
        hasPreviousPage,
        namespace,
    };

    return (
        <TableMailContext.Provider value={value}>
            {children}
        </TableMailContext.Provider>
    );
}

export default TableMailContext